import { Bill } from "@common/types/bill";
import { BillView } from "@common/types/views/billViews";
import { BillFilters } from "@common/contracts/bill.contract";
import { useCallback, useContext, useMemo, useState } from "react";
import { EagerLoaded } from "@common/types/base/orm";
import { useScope } from "@state/scopeContext.lib";
import { useUpdateBill } from "@api/bill.api";
import { pluralize } from "@common/helpers/string";
import { Pagination } from "@design-system/Pagination/Pagination";
import { HStack } from "@design-system/HStack/HStack";
import { Drawer } from "@design-system/Drawer/Drawer";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { CommandIslandTotal } from "@design-system/CommandIsland/CommandIslandTotal";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { PaginatedBillsTableContext } from "./PaginatedBillsTable.context";
import { PaginatedBillsTableContextProvider } from "./PaginatedBillsTableContextProvider";
import { BillsView } from "./views/billsView/BillsView";
import { BillDetails } from "./BillDetails";
import { ManualBillEditDrawer } from "./ManualBillEditDrawer/ManualBillEditDrawer";
import styles from "./BillsDrilldownDrawer.module.scss";

interface BillsDrilldownDrawerProps {
    view: BillView;
    open: boolean;
    onClose: () => void;
    headerAddon?: React.ReactNode | (() => React.ReactNode);
    parentFilters?: BillFilters;
    emptyView?: () => React.ReactNode;
}

const BillsDrilldownDrawerContent: React.FC<BillsDrilldownDrawerProps> = ({
    view,
    emptyView,
}) => {
    const [selectedBill, setSelectedBill] = useState<Bill>();
    const [editBill, setEditBill] = useState<Bill | null>(null);
    const { workspaceId } = useScope();
    const updateBillMutation = useUpdateBill();

    const handleUpdateMemo = useCallback(
        async (memo: string | null) => {
            if (!workspaceId || !selectedBill) {
                return;
            }
            await updateBillMutation.mutateAsync({
                workspaceId,
                billId: selectedBill.id,
                memo,
            });
        },
        [workspaceId, selectedBill, updateBillMutation],
    );

    const {
        bills,
        currentSort,
        handleSortChange,
        setPage,
        currentPage,
        totalPages,
        billCount,
    } = useContext(PaginatedBillsTableContext);

    const handleBillClick = useCallback((bill?: Bill) => {
        setSelectedBill(bill);
    }, []);

    const footer = (
        <CommandIsland
            sticky
            className={commandIslandStyles.bulkActionsStickyFooter}
        >
            <HStack gap={8}>
                {totalPages > 1 && (
                    <Pagination
                        size="md"
                        pageCount={totalPages}
                        currentPage={currentPage}
                        onPageChange={setPage}
                    />
                )}
                <CommandIslandTotal>
                    {billCount} {pluralize("bill", billCount)}
                </CommandIslandTotal>
            </HStack>
        </CommandIsland>
    );

    return (
        <div className={styles.content}>
            <BillsView
                view={view}
                bills={bills ?? null}
                selectedBillId={selectedBill?.id}
                onBillClick={handleBillClick}
                sort={currentSort}
                onSortChange={handleSortChange}
                emptyView={emptyView}
                footer={footer}
            />

            {selectedBill && (
                <BillDetails
                    bill={selectedBill as EagerLoaded<Bill, "lines">}
                    setBill={setSelectedBill}
                    onEdit={() => setEditBill(selectedBill)}
                    onUpdateMemo={handleUpdateMemo}
                    hideCloseButton={false}
                />
            )}

            {editBill && (
                <ManualBillEditDrawer
                    bills={[editBill]}
                    open
                    onClose={() => {
                        setEditBill(null);
                        setSelectedBill(undefined);
                    }}
                />
            )}
        </div>
    );
};

export const BillsDrilldownDrawer: React.FC<BillsDrilldownDrawerProps> = ({
    open,
    onClose,
    headerAddon,
    parentFilters,
    ...rest
}) => {
    const [isOpen, setIsOpen] = useState(false);

    const shouldShowContent = isOpen || open;

    const header = useMemo(() => {
        if (typeof headerAddon === "function") {
            return headerAddon();
        }
        return headerAddon;
    }, [headerAddon]);

    return (
        <Drawer
            open={open}
            onClose={onClose}
            direction="right"
            onAnimationEnd={setIsOpen}
            headerAddonLeft={header ?? "Bills"}
            noPadding
        >
            {shouldShowContent && (
                <PaginatedBillsTableContextProvider
                    overlayFilters={parentFilters}
                >
                    <BillsDrilldownDrawerContent
                        open={open}
                        onClose={onClose}
                        headerAddon={headerAddon}
                        parentFilters={parentFilters}
                        {...rest}
                    />
                </PaginatedBillsTableContextProvider>
            )}
        </Drawer>
    );
};
